import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { PurchaseNavButton } from './CheckoutFlow';
import { API_BASE } from '../lib/apiBase';
import analytics from '../lib/analytics';

const STAGES = [
  { id: 'received', label: 'Received' },
  { id: 'in_review', label: 'In Review' },
  { id: 'attorney_comments_added', label: 'Attorney Comments' },
  { id: 'ready_for_download', label: 'Ready for Download' },
  { id: 'completed', label: 'Completed' },
];

function formatCountdown(seconds) {
  if (seconds == null) return null;
  const overdue = seconds < 0;
  const abs = Math.abs(seconds);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const label = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return overdue ? `${label} overdue` : `${label} remaining`;
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

export default function AttorneyAssistTrack() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const justPaid = searchParams.get('paid') === '1';

  useEffect(() => {
    analytics.trackPageView(`track/${orderId}`);
    let cancelled = false;
    let timer;

    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/attorney-assist/track/${encodeURIComponent(orderId)}`);
        if (res.status === 404) throw new Error('Tracking ID not found');
        if (!res.ok) throw new Error('Could not load tracking status');
        const json = await res.json();
        if (!cancelled) {
          setData(json);
          setError(null);
          document.title = `Track ${orderId} · ImmigrateSmart`;
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    timer = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [orderId]);

  const stageIndex = STAGES.findIndex((s) => s.id === data?.status);

  return (
    <div className="min-h-screen bg-[#F4F3F0]">
      <nav className="bg-[#3A3A3F] px-4 sm:px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <a href="/" className="flex items-center text-white hover:opacity-90 min-w-0">
            <span className="wordmark-mark" aria-hidden="true"></span>
            <span className="wordmark text-lg text-white truncate">ImmigrateSmart</span>
          </a>
          <div className="flex items-center gap-3 shrink-0">
            <PurchaseNavButton />
            <Link to="/attorney-assist" className="text-white/80 hover:text-[#D4A017] text-sm">
              Attorney Assist
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <p className="text-[10px] tracking-[0.16em] uppercase text-[#B8860B] font-semibold mb-2">
          Order tracking
        </p>
        <h1 className="font-cormorant text-4xl text-[#222226] mb-2">Track your review</h1>
        <p className="text-sm text-[#3F3C38]/70 mb-8 font-mono">{orderId}</p>

        {justPaid && data?.status === 'pending_payment' && (
          <div className="mb-6 p-4 border border-[#D4A017]/40 bg-[#D4A017]/10 text-sm text-[#3F3C38]">
            Payment received — status will update to Received in a moment. This page refreshes automatically.
          </div>
        )}

        {loading && (
          <p className="text-sm text-[#3F3C38]/70">
            <i className="fas fa-spinner fa-spin mr-2" aria-hidden="true" />
            Loading status…
          </p>
        )}
        {error && (
          <p className="text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        {data && !error && (
          <>
            <div className="mb-10">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
                <p className="text-lg text-[#222226] font-semibold">{data.status_label}</p>
                <p className="text-xs text-[#3F3C38]/60">
                  Updated {formatWhen(data.updated_at)}
                </p>
              </div>

              <div className="flex gap-1 mb-3" aria-hidden="true">
                {STAGES.map((s, i) => {
                  const done = stageIndex >= 0 && i <= stageIndex;
                  const current = i === stageIndex;
                  return (
                    <div
                      key={s.id}
                      className={`h-2 flex-1 ${done ? 'bg-[#D4A017]' : 'bg-[#222226]/15'} ${current ? 'ring-1 ring-[#B8860B]' : ''}`}
                    />
                  );
                })}
              </div>
              <ol className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[10px] tracking-wide uppercase text-[#3F3C38]/55">
                {STAGES.map((s, i) => (
                  <li
                    key={s.id}
                    className={i === stageIndex ? 'text-[#B8860B] font-semibold' : ''}
                  >
                    {s.label}
                  </li>
                ))}
              </ol>
            </div>

            <dl className="space-y-4 text-sm text-[#3F3C38] mb-10">
              <div className="flex justify-between gap-4 border-b border-[#222226]/10 pb-3">
                <dt className="text-[#3F3C38]/60">Client</dt>
                <dd className="font-medium text-[#222226]">{data.client_name}</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-[#222226]/10 pb-3">
                <dt className="text-[#3F3C38]/60">Reviewed by</dt>
                <dd className="font-medium text-[#222226]">{data.attorney_firm}</dd>
              </div>
              {data.due_at && data.status !== 'completed' && data.status !== 'pending_payment' && (
                <div className="flex justify-between gap-4 border-b border-[#222226]/10 pb-3">
                  <dt className="text-[#3F3C38]/60">Delivery target</dt>
                  <dd className={`font-medium ${data.is_overdue ? 'text-red-700' : 'text-[#222226]'}`}>
                    {formatWhen(data.due_at)}
                    {data.seconds_remaining != null && (
                      <span className="block text-xs mt-0.5 font-normal">
                        {formatCountdown(data.seconds_remaining)}
                      </span>
                    )}
                  </dd>
                </div>
              )}
              {data.draft_filename && (
                <div className="flex justify-between gap-4 border-b border-[#222226]/10 pb-3">
                  <dt className="text-[#3F3C38]/60">Your draft</dt>
                  <dd className="font-medium text-[#222226] truncate max-w-[60%]">
                    {data.draft_filename}
                  </dd>
                </div>
              )}
            </dl>

            {data.download_available && data.download_url && (
              <a
                href={data.download_url.startsWith('http') ? data.download_url : `${API_BASE}${data.download_url}`}
                className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-3 bg-[#D4A017] text-[#222226] font-semibold text-sm tracking-wide hover:bg-[#E8B41F] transition-colors mb-8"
              >
                Download reviewed file
              </a>
            )}

            <p className="text-xs text-[#3F3C38]/60 leading-relaxed">
              Bookmark this page — it is your tracking link (like a shipping number). No login required.
              Status emails are also sent to the address used at checkout. Not legal advice.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
